import React, { useState, useEffect } from "react";
import {
  TextField, Button, Typography, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Box, Grid, InputAdornment, Alert, Chip,
  Divider, IconButton, Tooltip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import PhoneIcon from "@mui/icons-material/Phone";
import MessageIcon from "@mui/icons-material/Message";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScheduleSendIcon from "@mui/icons-material/ScheduleSend";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:4000";

const getStatusStyle = (status) => {
  const map = {
    sent: { bg: "rgba(16,185,129,0.08)", color: "#059669", label: "Delivered" },
    delivered: { bg: "rgba(16,185,129,0.08)", color: "#059669", label: "Delivered" },
    failed: { bg: "rgba(239,68,68,0.08)", color: "#dc2626", label: "Failed" },
    pending: { bg: "rgba(59,130,246,0.08)", color: "#2563eb", label: "Pending" },
    undelivered: { bg: "rgba(245,158,11,0.08)", color: "#d97706", label: "Undelivered" },
  };
  return map[status?.toLowerCase()] || { bg: "rgba(100,116,139,0.08)", color: "#64748b", label: status || "Unknown" };
};

export default function SmsPage({ token }) {
  const [to, setTo] = useState("");
  const [from, setFrom] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [message, setMessage] = useState(null);
  const [logsLoading, setLogsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const authToken = token || localStorage.getItem("token");

  const sendSms = async (e) => {
    e.preventDefault();
    setMessage(null);
    if (!to || !from || !body) { setMessage({ type: "error", text: "Please fill in all required fields." }); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/sms`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ to, from, body }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: "success", text: "SMS sent successfully!" });
        setTo(""); setFrom(""); setBody("");
        fetchLogs();
      } else {
        setMessage({ type: "error", text: data.error || "Failed to send SMS" });
      }
    } catch {
      setMessage({ type: "error", text: "Error sending SMS. Check your connection." });
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/sms/logs`, { headers: { Authorization: `Bearer ${authToken}` } });
      setLogs(await res.json());
      setLastUpdate(new Date());
    } catch (err) {
      console.error("Error fetching logs:", err);
    } finally {
      setLogsLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchLogs(); }, []);

  const stats = {
    total: logs.length,
    delivered: logs.filter((l) => ["sent", "delivered"].includes(l.status?.toLowerCase())).length,
    failed: logs.filter((l) => l.status?.toLowerCase() === "failed").length,
    pending: logs.filter((l) => l.status?.toLowerCase() === "pending").length,
  };

  const charCount = body.length;
  const smsCount = Math.ceil(charCount / 160) || 0;

  const statCards = [
    { label: "Delivered", value: stats.delivered, color: "#059669", bg: "rgba(16,185,129,0.06)", border: "rgba(16,185,129,0.15)", icon: <CheckCircleIcon sx={{ fontSize: 20 }} /> },
    { label: "Pending", value: stats.pending, color: "#2563eb", bg: "rgba(59,130,246,0.06)", border: "rgba(59,130,246,0.15)", icon: <ScheduleSendIcon sx={{ fontSize: 20 }} /> },
    { label: "Failed", value: stats.failed, color: "#dc2626", bg: "rgba(239,68,68,0.06)", border: "rgba(239,68,68,0.15)", icon: <ErrorIcon sx={{ fontSize: 20 }} /> },
    { label: "Total", value: stats.total, color: "#6366f1", bg: "rgba(99,102,241,0.06)", border: "rgba(99,102,241,0.15)", icon: <MessageIcon sx={{ fontSize: 20 }} /> },
  ];

  return (
    <div className="page-wrapper animate-in">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: "#0f172a", mb: 0.5 }}>SMS</Typography>
        <Typography sx={{ color: "#64748b", fontSize: "0.95rem" }}>Send and manage SMS messages</Typography>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3, borderRadius: "10px" }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left - Compose */}
        <Grid item xs={12} md={5}>
          <Box className="enterprise-card" sx={{ p: 3 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", color: "#0f172a", mb: 2.5, display: "flex", alignItems: "center", gap: 1 }}>
              <SendIcon sx={{ fontSize: 18, color: "#6366f1" }} /> Compose Message
            </Typography>
            <form onSubmit={sendSms}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#334155", mb: 0.8 }}>Recipient</Typography>
                  <TextField fullWidth size="small" required placeholder="+91 XXXXXXXXXX" value={to} onChange={(e) => setTo(e.target.value)} disabled={loading}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: "#94a3b8", fontSize: 18 }} /></InputAdornment> }}
                  />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#334155", mb: 0.8 }}>Sender</Typography>
                  <TextField fullWidth size="small" required placeholder="+91 XXXXXXXXXX" value={from} onChange={(e) => setFrom(e.target.value)} disabled={loading}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: "#94a3b8", fontSize: 18 }} /></InputAdornment> }}
                  />
                </Box>
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.8 }}>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#334155" }}>Message</Typography>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: charCount > 160 ? "#dc2626" : "#6366f1" }}>
                      {charCount}/160 ({smsCount} SMS)
                    </Typography>
                  </Box>
                  <TextField fullWidth size="small" required placeholder="Type your message..." multiline rows={4} value={body}
                    onChange={(e) => setBody(e.target.value)} disabled={loading} inputProps={{ maxLength: 320 }}
                  />
                </Box>
                <Button type="submit" variant="contained" fullWidth disabled={loading || !to || !from || !body}
                  startIcon={loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <SendIcon />}
                  sx={{ mt: 0.5, py: 1.3, fontSize: "0.9rem" }}
                >
                  {loading ? "Sending..." : "Send SMS"}
                </Button>
              </Box>
            </form>
          </Box>
        </Grid>

        {/* Right - Stats */}
        <Grid item xs={12} md={7}>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2, mb: 2 }}>
            {statCards.map((s) => (
              <Card key={s.label} sx={{ background: s.bg, border: `1px solid ${s.border}`, boxShadow: "none" }}>
                <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 }, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
                  <Box sx={{ color: s.color, mb: 1 }}>{s.icon}</Box>
                  <Typography sx={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 500, mb: 0.3 }}>{s.label}</Typography>
                  <Typography sx={{ color: s.color, fontWeight: 700, fontSize: "1.5rem" }}>{s.value}</Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
          <Card sx={{ background: "rgba(99,102,241,0.04)", border: "1px solid rgba(99,102,241,0.12)", boxShadow: "none" }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography sx={{ fontWeight: 600, color: "#0f172a", fontSize: "0.85rem", mb: 1 }}>Quick Tips</Typography>
              {["Each SMS can contain up to 160 characters", "Longer messages are split into multiple SMS", "Use valid Twilio phone numbers for sender"].map((t, i) => (
                <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 0.5 }}>
                  <Box sx={{ width: 4, height: 4, minWidth: 4, borderRadius: "50%", background: "#6366f1" }} />
                  <Typography sx={{ fontSize: "0.78rem", color: "#64748b" }}>{t}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Logs */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ color: "#0f172a", mb: 0.3 }}>Recent SMS Logs</Typography>
            <Typography sx={{ color: "#94a3b8", fontSize: "0.82rem" }}>Last updated: {lastUpdate.toLocaleTimeString()}</Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton onClick={() => { setRefreshing(true); fetchLogs(); }} disabled={refreshing} sx={{ background: "#f1f5f9" }}>
              <RefreshIcon sx={{ fontSize: 20, animation: refreshing ? "spin 1s linear infinite" : "none", "@keyframes spin": { "100%": { transform: "rotate(360deg)" } } }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Box className="enterprise-card" sx={{ p: 0, overflow: "hidden" }}>
          {logsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={32} sx={{ color: "#6366f1" }} />
            </Box>
          ) : logs.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <MessageIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }} />
              <Typography sx={{ fontWeight: 600, color: "#64748b" }}>No SMS logs yet</Typography>
              <Typography sx={{ color: "#94a3b8", fontSize: "0.85rem" }}>Send an SMS to see logs here</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead sx={{ background: "#f8fafc" }}>
                  <TableRow>
                    {["To", "From", "Message", "Status", "Sent Time"].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 600, color: "#64748b" }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => {
                    const s = getStatusStyle(log.status);
                    return (
                      <TableRow key={log.sid} sx={{ "&:hover": { background: "#fafafa" } }}>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <PhoneIcon sx={{ color: "#94a3b8", fontSize: 16 }} />
                            {log.to}
                          </Box>
                        </TableCell>
                        <TableCell>{log.from}</TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography noWrap sx={{ fontSize: "0.85rem" }} title={log.body}>{log.body}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={s.label} size="small" sx={{ background: s.bg, color: s.color, border: `1px solid ${s.color}20` }} />
                        </TableCell>
                        <TableCell sx={{ color: "#64748b", fontSize: "0.85rem" }}>
                          {log.dateSent ? new Date(log.dateSent).toLocaleString() : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </div>
  );
}
